import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { 
  CircleOfSoulsIcon, AlertIcon, CheckIcon, CloseIcon, LoadingIcon, BellIcon, 
  FeelIcon, ShieldIcon, SparkleIcon, SendIcon, MessageIcon
} from './BespokeIcons';
import { toast } from 'sonner';

/**
 * SpiralCouncilSOS - Request help from other users with consent
 * 
 * Flow:
 * 1. One person clicks "Request Spiral Council"
 * 2. All participants must consent
 * 3. If all consent, alert goes out to available helpers
 * 4. Helpers can join to assist
 */
const SpiralCouncilSOS = ({ 
  sessionId,
  participants = [],
  currentUserId,
  onCouncilRequested,
  onCouncilJoined,
  className = ''
}) => {
  const [open, setOpen] = useState(false);
  const [requestState, setRequestState] = useState('idle'); // idle, requesting, waiting, approved, denied
  const [consents, setConsents] = useState({});
  const [message, setMessage] = useState('');

  // Check if all participants have consented
  const allConsented = participants.length > 0 && 
    participants.every(p => consents[p.id] === true);

  // Handle consent from a participant
  const handleConsent = (participantId, agreed) => {
    setConsents(prev => ({ ...prev, [participantId]: agreed }));
    
    if (!agreed) {
      setRequestState('denied');
      toast.info('A participant declined. The council request has been cancelled.');
      setTimeout(() => {
        setRequestState('idle');
        setConsents({});
      }, 3000);
    }
  };

  // Initialize a council request
  const initiateRequest = () => {
    setRequestState('requesting');
    
    // Auto-consent for the requester
    setConsents({ [currentUserId]: true });
    
    // In a real app, this would send notifications to other participants
    toast.success('Spiral Council request initiated! Waiting for others to consent...');
    
    // Simulate waiting for consents (in real app, this would be WebSocket/polling)
    setRequestState('waiting');
  };

  // Send out the SOS to available helpers
  const sendSOS = async () => {
    setRequestState('approved');
    
    try {
      // In a real app, this would call an API to notify available helpers
      // await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/spiral-council/request`, {...})
      
      toast.success('Spiral Council alert sent! Helpers will join soon.');
      onCouncilRequested?.({
        sessionId,
        participants: participants.map(p => p.name),
        message
      });
    } catch (error) {
      toast.error('Failed to send council request. Please try again.');
    }
  };

  // Effect to check if all consented and auto-send
  useEffect(() => {
    if (requestState === 'waiting' && allConsented) {
      sendSOS();
    }
  }, [allConsented, requestState]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={`gap-1.5 border-rose-500/30 text-rose-400 hover:bg-rose-500/10 hover:text-rose-300 ${className}`}
          data-testid="spiral-council-trigger"
        >
          <CircleOfSoulsIcon size={16} color="#FB7185" />
          <span className="text-xs">Spiral Council</span>
        </Button>
      </DialogTrigger>

      <DialogContent className="bg-[#0f1419] border-white/10 text-white max-w-md" data-testid="spiral-council-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg" style={{ fontFamily: "'Playfair Display', serif" }}>
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-rose-500/20 to-purple-600/10 border border-rose-500/30 flex items-center justify-center">
              <CircleOfSoulsIcon size={16} color="#FB7185" />
            </div>
            Spiral Council
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Explanation */}
          <div className="bg-white/5 rounded-lg p-3 border border-white/10">
            <p className="text-sm text-white/70 leading-relaxed">
              <strong className="text-rose-400">Need support?</strong> Request a Spiral Council—experienced 
              facilitators who can help guide your conversation with compassion.
            </p>
            <p className="text-xs text-white/50 mt-2">
              ⚠️ All participants must consent before helpers are notified.
            </p>
          </div>

          {/* Idle State - Show request form */}
          {requestState === 'idle' && (
            <>
              <div>
                <label className="text-xs text-white/50 mb-1 block">What kind of support do you need? (optional)</label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="e.g., We're stuck and could use a neutral perspective..."
                  className="w-full bg-white/5 border border-white/20 rounded-lg p-2 text-sm text-white placeholder:text-white/30 min-h-[80px]"
                />
              </div>

              <Button
                onClick={initiateRequest}
                className="w-full bg-gradient-to-r from-rose-500 to-purple-600 hover:from-rose-400 hover:to-purple-500 text-white font-medium"
              >
                <BellIcon size={16} color="#FFFFFF" className="mr-2" />
                Request Spiral Council
              </Button>
            </>
          )}

          {/* Waiting State - Show consent status */}
          {(requestState === 'requesting' || requestState === 'waiting') && (
            <div className="space-y-3">
              <p className="text-sm text-amber-400 flex items-center gap-2">
                <LoadingIcon size={16} color="#FBBF24" />
                Waiting for all participants to consent...
              </p>

              <div className="space-y-2">
                {participants.map((p) => (
                  <div 
                    key={p.id}
                    className="flex items-center justify-between p-2 bg-white/5 rounded-lg border border-white/10"
                  >
                    <span className="text-sm text-white/80">{p.name}</span>
                    
                    {consents[p.id] === true ? (
                      <span className="flex items-center gap-1 text-xs text-green-400">
                        <CheckIcon size={12} color="#4ADE80" /> Consented
                      </span>
                    ) : consents[p.id] === false ? (
                      <span className="flex items-center gap-1 text-xs text-red-400">
                        <CloseIcon size={12} color="#F87171" /> Declined
                      </span>
                    ) : (
                      // Show consent buttons for the current user if they haven't consented yet
                      p.id === currentUserId ? (
                        <div className="flex gap-1">
                          <button
                            onClick={() => handleConsent(p.id, true)}
                            className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs hover:bg-green-500/30"
                          >
                            <CheckIcon size={12} color="#4ADE80" />
                          </button>
                          <button
                            onClick={() => handleConsent(p.id, false)}
                            className="px-2 py-1 bg-red-500/20 text-red-400 rounded text-xs hover:bg-red-500/30"
                          >
                            <CloseIcon size={12} color="#F87171" />
                          </button>
                        </div>
                      ) : (
                        <span className="text-xs text-white/40 flex items-center gap-1">
                          <LoadingIcon size={12} color="rgba(255,255,255,0.4)" /> Waiting...
                        </span>
                      )
                    )}
                  </div>
                ))}
              </div>

              <Button
                variant="outline"
                onClick={() => {
                  setRequestState('idle');
                  setConsents({});
                }}
                className="w-full border-white/20 text-white/60"
              >
                Cancel Request
              </Button>
            </div>
          )}

          {/* Approved State */}
          {requestState === 'approved' && (
            <div className="text-center py-4">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-green-500/20 to-emerald-600/10 border border-green-500/30 flex items-center justify-center">
                <CheckIcon size={32} color="#4ADE80" />
              </div>
              <h3 className="text-lg text-green-400 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                Council Request Sent!
              </h3>
              <p className="text-sm text-white/60">
                Available helpers have been notified. Someone will join your session soon.
              </p>
              <Button
                onClick={() => {
                  setOpen(false);
                  setRequestState('idle');
                  setConsents({});
                }}
                className="mt-4 bg-white/10 hover:bg-white/20 text-white"
              >
                Close
              </Button>
            </div>
          )}

          {/* Denied State */}
          {requestState === 'denied' && (
            <div className="text-center py-4">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-red-500/20 to-rose-600/10 border border-red-500/30 flex items-center justify-center">
                <CloseIcon size={32} color="#F87171" />
              </div>
              <h3 className="text-lg text-red-400 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                Request Declined
              </h3>
              <p className="text-sm text-white/60">
                A participant declined the council request. That's okay—you can always try again later.
              </p>
            </div>
          )}

          {/* Footer note */}
          <p className="text-[10px] text-white/30 text-center">
            Spiral Council members are volunteers trained in NVC facilitation.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SpiralCouncilSOS;
